import Vue from 'vue';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    onBeforeMount,
    onMounted,
    watch,
} from '@vue/composition-api';

import { RouteName } from '@app/router/constants/RouteName';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { marketingSourceService } from '@core/services/marketing/MarketingSourceService';
import authenticationStoreIndex from '@store/modules/data/authentication/authenticationIndex';
import { RegisterResponse } from '@core/services/authentication/_private/Types';
import store from '@store/index';
import {
    AnalyticsProtoEvents,
    AnalyticsEventTypes,
    AnalyticsScreenNames,
    AnalyticsModalNames,
} from '@app/utils/analyticsScreenNames';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';

import { getDarklyExpId } from '@app/shared/featureFlags/isAlternativeRegistrationEnabled';
import { getIsMojeIdEnabled } from '@accountFederation/modules/dynamicForm/utils';
import {
    createLanderRegistrationResultAnalyticsPayload,
    retrieveLanderCookie,
} from '@src/app/utils/landerAnalytics';
import { AnalyticsAction } from '@superbet-group/web.types';
import { getPayloadRegistrationType } from '@src/app/utils/analyticsEvents';
import { getRegistrationType } from './registrationType';
import { RegistrationResult } from '@superbet-group/product.analytics';

export function useRegisterLogic(isRegisterStartPoint = true) {
    const authenticationStore = useVuexStore<typeof authenticationStoreIndex>('data/authentication');
    const user = authenticationStore.getter('user');
    const { pushRoute, route } = useVueRouter();
    watch(user, (currentUser) => {
        if (currentUser) {
            pushRoute({ name: RouteName.App.Home });
        }
    }, {
        immediate: true,
    });

    const registerAction = authenticationStore.action('register');
    const loginAction = authenticationStore.action('login');
    async function register(data: Record<string, unknown>): Promise<RegisterResponse> {
        try {
            const registrationResponse = await registerAction(data);
            onRegister(registrationResponse);
            return registrationResponse;
        } catch (e: any) {
            if (e.message !== 'geoLocationError') {
                recordRegisterError(e as Error);
            }
            throw e;
        }
    }

    function recordRegisterAttempt(): void {
        AnalyticsSDK.getInstance().log('Register_Attempt');
    }

    function onRegister(registrationResponse: RegisterResponse): void {
        const isScreenOpenAccountOpenSent = shellLocalConfig.isScreenOpenAccountOpenSent;
        if (isScreenOpenAccountOpenSent) {
            sendAccountCreatedToClickstream(registrationResponse);
        }
        notifyForzaRegistrationIsSuccessful();
        marketingSourceService.clearMarketingData();
        store.dispatch('data/authentication/setIsReturningUserDevice');
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
        // @TODO(corneliu-iancu): Temporary dual analytics implementation.
        // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
        // Both libraries are being used in parallel to validate data consistency between them.
        // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
        // Once validation is complete, $superAnalytics will be deprecated and removed.
        AnalyticsSDK.getInstance().log('registration_request_result', {
            registration_result: RegistrationResult.REGISTRATION_RESULT_SUCCESS,
            registration_player_code: registrationResponse?.data?.PlayerId ?? '',
            registration_type: getPayloadRegistrationType(getRegistrationType()),
            content_lander_hash: retrieveLanderCookie(),
        });
        Vue.prototype.$superAnalytics.sendEvent(
            createLanderRegistrationResultAnalyticsPayload({
                playerCode: registrationResponse?.data?.PlayerId,
            }),
        );
    }

    function recordRegisterError(e: Error): void {
        AnalyticsSDK.getInstance().log('Registration_Error', {
            errorMessage: e.message || 'Registration error',
            darklyExpId: getDarklyExpId(),
        });
        // @TODO(corneliu-iancu): Temporary dual analytics implementation.
        // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
        // Both libraries are being used in parallel to validate data consistency between them.
        // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
        // Once validation is complete, $superAnalytics will be deprecated and removed.
        AnalyticsSDK.getInstance().log('registration_request_result', {
            registration_result: RegistrationResult.REGISTRATION_RESULT_FAIL,
            registration_type: getPayloadRegistrationType(getRegistrationType()),
            fail_reason: e.message || 'Registration error',
            content_lander_hash: retrieveLanderCookie(),
        });
        Vue.prototype.$superAnalytics.sendEvent(
            createLanderRegistrationResultAnalyticsPayload({
                error: e,
            }),
        );
    }

    function notifyForzaRegistrationIsSuccessful(): void {
        const windowRef = window as any;
        windowRef.webkit?.messageHandlers?.eventFromJsToNativeApp?.postMessage?.({
            eventName: 'registrationComplete',
        });

        windowRef.NativeApp?.eventFromJsToNativeApp?.();
    }

    function sendAccountCreatedToClickstream(registrationResponse: RegisterResponse): void {
        // @TODO(corneliu-iancu): Temporary dual analytics implementation.
        // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
        // Both libraries are being used in parallel to validate data consistency between them.
        // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
        // Once validation is complete, $superAnalytics will be deprecated and removed.
        AnalyticsSDK.getInstance().log('account_open', {
            screen_name: AnalyticsScreenNames.registration,
            modal_name: AnalyticsModalNames.accountCreated,
            user_id_registration: registrationResponse.data.PlayerId?.toString() ?? '',
            transaction_id: '',
        });
        const payload = {
            eventType: AnalyticsEventTypes.accountOpen,
            screenOpenAccountOpen: {
                screenName: AnalyticsScreenNames.registration,
                modalName: AnalyticsModalNames.accountCreated,
                userIdRegistration: registrationResponse.data.PlayerId?.toString() ?? '',
            },
            AnalyticsEvent: AnalyticsProtoEvents.screenOpenAccountOpen,
        };
        Vue.prototype.$superAnalytics.sendEvent(payload);
    }

    function saveNewBonusCode(): void {
        if (route.value.query.bonus) {
            clientStorageWithExpiry.set('bonusCode', route.value.query.bonus, 30);
        }
    }

    async function loginAfterRegistration(username: string, password: string): Promise<void> {
        await loginAction({
            username,
            password,
        });
    }

    async function registrationOpenEventHandler() {
        const { route } = useVueRouter();
        const shouldSendEvent = isRegisterStartPoint || !(await getIsMojeIdEnabled(route));

        if (shouldSendEvent) {
            AnalyticsSDK.getInstance().log('Registration_Step1', {
                registrationMethod: AnalyticsAction.Classic,
                darklyExpId: getDarklyExpId(),
            });
        }
    }

    onBeforeMount(saveNewBonusCode);
    onMounted(registrationOpenEventHandler);

    return {
        register,
        loginAfterRegistration,
        recordRegisterAttempt,
    };
}
